import { useEffect, useMemo, useState, useRef } from 'react';
import {
	Box,
	Checkbox,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { getPaymentCategories } from 'services/beneficiary.services';
import { PaymentCategoryOption } from 'constants/beneficiary.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { formatPaymentCategory } from 'utils/paymentCategoryUtils';

type CommonProps = {
	error?: boolean;
	helperText?: string;
	disabled?: boolean;
	label?: string;
	hideTitle?: boolean;
	required?: boolean;
	options?: PaymentCategoryOption[];
};

type MultiProps = CommonProps & {
	value: string[];
	onChange: (value: string[]) => void;
	multiple?: true;
};

type SingleProps = CommonProps & {
	value: string;
	onChange: (value: string) => void;
	multiple: false;
};

type PaymentCategoryPickerProps = MultiProps | SingleProps;

export default function PaymentCategoryPicker(props: PaymentCategoryPickerProps) {
	const {
		value,
		onChange,
		error,
		helperText,
		disabled,
		label,
		hideTitle = false,
		required = false,
		options: externalOptions,
	} = props;
	const isSingle = props.multiple === false;
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const [options, setOptions] = useState<PaymentCategoryOption[]>(externalOptions || []);
	const [loading, setLoading] = useState<boolean>(false);
	const selectRef = useRef<HTMLDivElement | null>(null);
	const [noOptions, setNoOptions] = useState<boolean>(false);

	// Fetch categories on mount OR use external options; guard with `active` to avoid setting state after unmount; auto-select sole option in both single and multi mode
	useEffect(() => {
		let active = true;

		const handleOptions = (list: PaymentCategoryOption[]) => {
			if (!active) return;
			setOptions(list);
			setNoOptions(!list || list.length === 0);

			// Auto-select if there's only one category available and no selection has been made
			if (list.length === 1) {
				let hasSelection = false;

				if (isSingle) {
					// For single-select mode: check if a value is already selected
					hasSelection = Boolean(value);
				} else {
					// For multi-select mode: check if any value is already selected
					if (Array.isArray(value)) {
						hasSelection = value.length > 0;
					} else {
						hasSelection = Boolean(value);
					}
				}

				// Auto-select the single available option if no selection exists
				if (!hasSelection) {
					if (isSingle) {
						(onChange as (value: string) => void)(list[0].id);
					} else {
						(onChange as (val: string[]) => void)([list[0].id]);
					}
				}
			}
		};

		if (externalOptions) {
			handleOptions(externalOptions);
		} else {
			setLoading(true);
			getPaymentCategories()
				.then((list) => {
					handleOptions(list);
				})
				.finally(() => {
					if (active) setLoading(false);
				});
		}

		return () => {
			active = false;
		};
	}, [externalOptions]); // Re-run if external options change

	// Build lookups for labels and full options
	const { labels, optionMap } = useMemo(() => {
		return options.reduce(
			(acc, option) => {
				acc.labels[option.id] = option.label;
				acc.optionMap[option.id] = option;
				return acc;
			},
			{ labels: {} as Record<string, string>, optionMap: {} as Record<string, PaymentCategoryOption> }
		);
	}, [options]);

	const renderMultiValue = (selected: string[]) => {
		if (!selected.length) return '';
		return selected.map((item) => formatPaymentCategory(optionMap[item], item, t)).join(', ');
	};

	const renderSingleValue = (selected: string | string[]) => {
		const val = Array.isArray(selected) ? selected[0] : selected;
		return val ? formatPaymentCategory(optionMap[val], val, t) : '';
	};

	const selectValue: string | string[] = useMemo(() => {
		if (!isSingle) {
			return Array.isArray(value) ? value : value ? [value] : [];
		}
		return Array.isArray(value) ? value[0] || '' : value || '';
	}, [isSingle, value]);

	const handleChange = (event: SelectChangeEvent<string | string[]>) => {
		const {
			target: { value: selected },
		} = event;
		const nextSelection = typeof selected === 'string' ? selected.split(',') : selected;
		if (isSingle) {
			(onChange as (value: string) => void)(nextSelection[0] || '');
			return;
		}
		(onChange as (value: string[]) => void)(nextSelection as string[]);
	};

	return (
		<Stack spacing={1}>
			{!hideTitle && (
				<Typography variant="h3" pb={2}>
					{t('page.beneficiary.create.step.3.payment_categories.title')}
				</Typography>
			)}
			<FormControl
				fullWidth
				disabled={disabled || loading || noOptions}
				error={error || noOptions}
				required={required}
			>
				<InputLabel
					id="beneficiary-payment-category-label"
					sx={{
						color: theme.palette.root.basicDeepGreen,
						'&.Mui-focused': { color: theme.palette.root.basicDeepGreen },
					}}
				>
					{label || t('page.beneficiary.create.step.2.payment_categories.label')}
				</InputLabel>
				<Select
					labelId="beneficiary-payment-category-label"
					multiple={!isSingle}
					value={selectValue as any}
					onChange={handleChange}
					input={<OutlinedInput label={label} />}
					ref={selectRef}
					renderValue={isSingle ? renderSingleValue : renderMultiValue}
					MenuProps={{
						PaperProps: {
							style: {
								direction: 'rtl',
								width: selectRef.current ? selectRef.current.clientWidth : undefined,
							},
						},
					}}
				>
					{options.map((option) => (
						<MenuItem
							key={option.id}
							value={option.id}
							sx={{
								display: 'flex',
								justifyContent: 'flex-start',
								gap: 1,
								typography: 'subtitle1',
								fontSize: theme.typography.subtitle1.fontSize,
							}}
						>
							{!isSingle && Array.isArray(selectValue) && (
								<Checkbox checked={selectValue.includes(option.id)} />
							)}
							<Box
								sx={{
									flex: 1,
									lineHeight: 1.4,
									whiteSpace: 'normal',
									wordBreak: 'break-word',
									overflowWrap: 'anywhere',
								}}
							>
								{formatPaymentCategory(option, option.id, t)}
							</Box>
						</MenuItem>
					))}
				</Select>
				{loading && (
					<FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<CircularProgress size={14} /> {t('validations.loading.text', 'טוען קטגוריות')}
					</FormHelperText>
				)}
				{!loading && noOptions && (
					<FormHelperText>{t('page.beneficiary.create.step.3.payment_categories.empty')}</FormHelperText>
				)}
				{!loading && !noOptions && helperText && <FormHelperText>{helperText}</FormHelperText>}
			</FormControl>
		</Stack>
	);
}

export function MultiPaymentCategoryPicker(props: Omit<MultiProps, 'multiple'>) {
	return <PaymentCategoryPicker {...props} multiple />;
}

export function SinglePaymentCategoryPicker(props: Omit<SingleProps, 'multiple'>) {
	return <PaymentCategoryPicker {...props} multiple={false} />;
}
